import { useMemo, useState } from 'react';
import { ActionButton, type FilterGroup } from '@practium/ui';
import { PARAM_LABELS, SOIL_GROUP_CODES, SOIL_KIND_LABELS } from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type { ReferenceSectionProps, SoilCard, SoilGroupKey } from '../../types/parameters.types';
import { formatNumber, pluralize } from '../../utils/format';
import { buildSoilCards, listIncompleteParams, removeSoilCard } from '../../utils/soilCards';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';
import { IncompleteParamsModal } from './IncompleteParamsModal';
import { SoilRuleModal } from './SoilRuleModal';
import styles from './Sections.module.css';

interface SoilSectionProps extends ReferenceSectionProps {
	group: SoilGroupKey;
}

type ModalState = { type: 'closed' } | { type: 'rule'; card: SoilCard | null } | { type: 'incomplete' };

const SAVED_MESSAGE = 'Regra salva com sucesso!';

const secondary = (value: string | null | undefined) => <span className={tableStyles.cellSecondary}>{value || '—'}</span>;

const COLUMNS: ReferenceColumn<SoilCard>[] = [
	{
		key: 'param',
		header: 'Parâmetro',
		render: (card) => <span className={tableStyles.cellPrimary}>{card.title}</span>,
	},
	{ key: 'condition', header: 'Condição', render: (card) => secondary(card.meta) },
	{ key: 'ideal', header: 'Faixa ideal', render: (card) => secondary(card.idealRange) },
	{ key: 'unit', header: 'Unidade', render: (card) => secondary(card.unit) },
	{ key: 'method', header: 'Método', render: (card) => secondary(card.method) },
	{
		key: 'limits',
		header: 'Limites das classes',
		render: (card) =>
			secondary(
				card.classes
					.slice(0, -1)
					.map((item) => formatNumber(item.maxValue))
					.join(' · ')
			),
	},
	{
		key: 'status',
		header: 'Situação',
		render: (card) => (
			<span className={`${tableStyles.badge} ${card.isComplete ? tableStyles.badgeHigh : tableStyles.badgeMedium}`}>
				{card.isComplete ? 'Completa' : 'Incompleta'}
			</span>
		),
	},
];

const getSearchText = (card: SoilCard) => `${card.title} ${card.code} ${card.meta} ${card.method ?? ''}`;

const getFilterTags = (card: SoilCard) => [
	`param:${card.code}`,
	`kind:${card.kind}`,
	`status:${card.isComplete ? 'complete' : 'incomplete'}`,
];

const buildFilterGroups = (group: SoilGroupKey): FilterGroup[] => [
	{
		label: 'Parâmetro',
		options: SOIL_GROUP_CODES[group].map((code) => ({ value: `param:${code}`, label: PARAM_LABELS[code] ?? code })),
	},
	{
		label: 'Tipo de regra',
		options: Object.entries(SOIL_KIND_LABELS).map(([value, label]) => ({ value: `kind:${value}`, label })),
	},
	{
		label: 'Situação',
		options: [
			{ value: 'status:complete', label: 'Completa' },
			{ value: 'status:incomplete', label: 'Incompleta' },
		],
	},
];

export const SoilSection = ({ group, reference, isSaving, onSave, onConfirmDelete }: SoilSectionProps) => {
	const [modal, setModal] = useState<ModalState>({ type: 'closed' });
	const cards = useMemo(() => buildSoilCards(reference, group), [reference, group]);
	const filterGroups = useMemo(() => buildFilterGroups(group), [group]);
	const incomplete = useMemo(() => listIncompleteParams(reference), [reference]);
	const list = useTableList({ items: cards, filterGroups, getSearchText, getFilterTags });
	const closeModal = () => setModal({ type: 'closed' });

	const handleDelete = async (card: SoilCard) => {
		if (await onConfirmDelete(`a regra de ${card.title} (${card.meta})`)) {
			await onSave(removeSoilCard(reference, card), 'Regra excluída com sucesso!');
		}
	};

	return (
		<>
			<SectionList
				list={list}
				columns={COLUMNS}
				getRowId={(card) => card.id}
				unit={['regra', 'regras']}
				searchPlaceholder="Buscar por parâmetro, condição ou método..."
				actionLabel="Nova regra"
				notice={
					incomplete.length > 0 && (
						<div className={styles.notice}>
							<span>
								{pluralize(incomplete.length, 'parâmetro do solo está', 'parâmetros do solo estão')} sem método,
								unidade ou faixa ideal. Complete o cadastro para conseguir salvar alterações nas regras.
							</span>
							<ActionButton text="Completar" onClick={() => setModal({ type: 'incomplete' })} />
						</div>
					)
				}
				onAction={() => setModal({ type: 'rule', card: null })}
				onEdit={(card) => setModal({ type: 'rule', card })}
				onDelete={handleDelete}
			/>
			{modal.type === 'rule' && (
				<SoilRuleModal
					group={group}
					reference={reference}
					card={modal.card}
					isSaving={isSaving}
					onClose={closeModal}
					onSubmit={(update) => onSave(update, SAVED_MESSAGE)}
				/>
			)}
			{modal.type === 'incomplete' && (
				<IncompleteParamsModal
					reference={reference}
					params={incomplete}
					isSaving={isSaving}
					onClose={closeModal}
					onSubmit={(update) => onSave(update, 'Cadastro do solo atualizado com sucesso!')}
				/>
			)}
		</>
	);
};
